import { UseFormReturn, useController } from 'react-hook-form'

import { CheckEligibilityFormSchema } from '../../types'
import ConnectWalletIcons from '@/ui/assets/spk-airdrop/connect-wallet-icons.svg?react'
import { Button, ButtonProps } from '@/ui/atoms/button/Button'
import { Form } from '@/ui/atoms/form/Form'
import { Panel } from '@/ui/atoms/panel/Panel'
import { AddressInput } from '@/ui/molecules/address-input/AddressInput'
import { cn } from '@/ui/utils/style'

export interface CheckEligibilityPanelProps {
  form: UseFormReturn<CheckEligibilityFormSchema>
  isEligibilityFetching: boolean
  eligiblityCheckEnabled: boolean
  eligibilityCheckError: string | undefined
  onCheck: () => void
  onConnectWallet: () => void
}

export function CheckEligibilityPanel({
  form,
  onCheck,
  isEligibilityFetching,
  eligiblityCheckEnabled,
  eligibilityCheckError,
  onConnectWallet,
}: CheckEligibilityPanelProps) {
  const { field } = useController({ control: form.control, name: 'address' })
  const checkButtonProps: Omit<ButtonProps, 'size'> = {
    onClick: onCheck,
    loading: isEligibilityFetching,
    disabled: !eligiblityCheckEnabled,
    children: 'Check',
    className: cn(!eligiblityCheckEnabled && 'before:bg-tertiary!', isEligibilityFetching && 'bg-tertiary'),
  }

  return (
    <Panel spacing="l" className="flex flex-col gap-5 rounded-md sm:gap-8">
      <div className="flex flex-col gap-4">
        <div className="typography-heading-4 text-primary">Check if you were eligible for SPK Airdrop</div>
        <div className="typography-body-4 text-secondary">
          Enter your wallet address below to check if you were eligible for an SPK Airdrop. You can also connect your
          wallet directly.
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Form {...form}>
            <AddressInput
              {...field}
              label="Enter address manually"
              placeholder="Address"
              error={form.formState.errors.address?.message ?? eligibilityCheckError}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && eligiblityCheckEnabled && !isEligibilityFetching) {
                  e.preventDefault()
                  onCheck()
                }
              }}
              postFix={
                <>
                  <Button
                    size="l"
                    {...checkButtonProps}
                    className={cn('my-2 mr-2 hidden px-5 sm:block', checkButtonProps.className)}
                  />
                  <Button
                    size="m"
                    {...checkButtonProps}
                    className={cn('mr-1 px-5 sm:hidden', checkButtonProps.className)}
                  />
                </>
              }
            />
          </Form>
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="h-px bg-secondary" />
          <div className="typography-label-3 px-6 py-3 text-secondary">OR</div>
          <div className="h-px bg-secondary" />
        </div>
        <Button
          variant="secondary"
          size="l"
          className="flex w-full items-center justify-center gap-0"
          onClick={onConnectWallet}
        >
          <ConnectWalletIcons className="h-7" />
          Connect wallet
        </Button>
      </div>
    </Panel>
  )
}
